import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { formatDate, isOverdue } from '@/lib/format';
import { useMoney } from '@/hooks/useMoney';
import { useLayout } from '@/hooks/useMediaQuery';
import { StatusSummary } from '@/components/StatusSummary';
import { AGEING_BUCKETS } from '@/lib/ageing';
import { ResponsiveRecords } from '@/components/ResponsiveRecords';
import {
  Card,
  EmptyState,
  ErrorState,
  Money,
  PageHeader,
  Pagination,
  Spinner,
  StatusBadge,
  Table,
  Tabs,
  ToneBadge,
} from '@/components/ui';

type View =
  'ALL' | 'DUE_TODAY' | 'DUE_THIS_WEEK' | 'OVERDUE' | 'APPROVED' | 'PAYMENT_PENDING' | 'PAID';

const VIEWS: Array<{ key: View; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'DUE_TODAY', label: 'Due today' },
  { key: 'DUE_THIS_WEEK', label: 'Due this week' },
  { key: 'OVERDUE', label: 'Overdue' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'PAYMENT_PENDING', label: 'Payment pending' },
  { key: 'PAID', label: 'Paid' },
];

type Source = 'ALL' | 'INVOICES' | 'LOANS';

/** Where a row opens: an invoice, or a loan EMI's loan in Payment Accounts. */
const hrefOf = (row: { id: string; payableOf?: 'LOAN'; loanId?: string }) =>
  row.payableOf === 'LOAN'
    ? `/settings/payment-accounts/loans?loan=${row.loanId ?? ''}`
    : `/invoices/${row.id}`;

/** Accounts payable — PRD §16, with the ageing summary from §32. Loan EMIs sit beside invoices. */
export function PayablesPage() {
  const { companyId, verticalId, can } = useAuth();
  const [source, setSource] = useState<Source>('ALL');
  const money = useMoney();
  const layout = useLayout();
  const [view, setView] = useState<View>('ALL');
  const [page, setPage] = useState(1);

  const { data: summary } = useQuery({
    queryKey: ['payables', 'summary', companyId, verticalId],
    queryFn: () => api.payables.summary({ companyId, verticalId }),
  });

  const { data: ageing } = useQuery({
    queryKey: ['payables', 'ageing', companyId, verticalId],
    queryFn: () => api.payables.ageing({ companyId, verticalId }),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['payables', companyId, verticalId, view, source, page],
    queryFn: () => api.payables.list({ companyId, verticalId, view, source, page, pageSize: 25 }),
  });

  return (
    <>
      <PageHeader title="Accounts Payable" subtitle="What we owe, and when it is due" />

      {ageing && layout !== 'desktop' ? (
        // Phone and tablet: the six figures as one short card — the total open,
        // a bar split by age, then each bucket's amount and count.
        <StatusSummary
          title="Payables ageing"
          headline={money.compact(ageing.total?.amount ?? 0)}
          caption="open"
          aside={`${ageing.total?.count ?? 0} open invoice${ageing.total?.count === 1 ? '' : 's'}`}
          unit={['invoice', 'invoices']}
          segments={AGEING_BUCKETS.map((bucket) => ({
            key: bucket.key,
            label: bucket.label,
            colour: bucket.colour,
            count: ageing[bucket.key]?.count ?? 0,
            value: money.compact(ageing[bucket.key]?.amount ?? 0),
            weight: ageing[bucket.key]?.amount ?? 0,
          }))}
        />
      ) : ageing ? (
        // All six on a wide screen.
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-6">
          {(['NOT_DUE', '1_30', '31_60', '61_90', '90_PLUS'] as const).map((bucket) => (
            <Card key={bucket} className="p-4">
              <p className="stat-label">
                {bucket === 'NOT_DUE'
                  ? 'Not due'
                  : bucket === '90_PLUS'
                    ? '90+ days'
                    : `${bucket.replace('_', '–')} days`}
              </p>
              <p
                className={`mt-1 text-base font-semibold tabular sm:text-xl ${bucket === '90_PLUS' ? 'text-red-700' : ''}`}
              >
                {money.compact(ageing[bucket]?.amount ?? 0)}
              </p>
              <p className="text-xs text-slate-500">{ageing[bucket]?.count ?? 0} invoices</p>
            </Card>
          ))}
          <Card variant="dark" className="p-4">
            <p className="stat-label text-ink-300">Total open</p>
            <p className="mt-1 text-base font-semibold tabular sm:text-xl">
              {money.compact(ageing.total?.amount ?? 0)}
            </p>
            <p className="text-xs text-ink-300">{ageing.total?.count ?? 0} invoices</p>
          </Card>
        </div>
      ) : null}

      <div className="card">
        {layout === 'desktop' ? (
          <Tabs
            tabs={VIEWS.map((entry) => ({ ...entry, count: summary?.[entry.key]?.count }))}
            active={view}
            onChange={(key) => {
              setView(key);
              setPage(1);
            }}
          />
        ) : (
          // Seven tabs wrap onto several lines on a narrow screen; one list does not.
          <label className="block border-b border-ink-100 p-4">
            <span className="label">Show</span>
            <select
              className="input"
              value={view}
              onChange={(event) => {
                setView(event.target.value as View);
                setPage(1);
              }}
            >
              {VIEWS.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                  {summary?.[entry.key]?.count !== undefined
                    ? ` (${summary[entry.key]!.count})`
                    : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {can('loan:read') ? (
          <div className="flex items-center justify-end gap-2 border-b border-ink-100 px-4 py-2">
            <label htmlFor="payable-source" className="text-sm text-ink-600">
              Payables from
            </label>
            <select
              id="payable-source"
              className="input w-auto"
              value={source}
              onChange={(event) => {
                setSource(event.target.value as Source);
                setPage(1);
              }}
            >
              <option value="ALL">Invoices and loan EMIs</option>
              <option value="INVOICES">Invoices</option>
              <option value="LOANS">Loan EMIs</option>
            </select>
          </div>
        ) : null}

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4">
            <ErrorState error={error} />
          </div>
        ) : !data?.items.length ? (
          <EmptyState illustration="wallet" title="Nothing in this view" />
        ) : (
          <>
            <ResponsiveRecords
              layout={layout}
              openLabel="Open invoice"
              records={data.items.map((invoice) => {
                const late = isOverdue(invoice.dueDate) && invoice.status !== 'PAID';
                return {
                  key: invoice.id,
                  href: hrefOf(invoice),
                  title: invoice.vendorName ?? '—',
                  badge: <PayableStatus row={invoice} />,
                  details: [
                    {
                      label: invoice.payableOf === 'LOAN' ? 'Loan EMI' : 'Invoice',
                      value: invoice.invoiceNumber ?? '—',
                    },
                    { label: 'Amount', value: <Money minor={invoice.totalAmount} /> },
                    {
                      label: 'Due',
                      value: formatDate(invoice.dueDate),
                      className: late ? 'text-red-600' : undefined,
                    },
                  ],
                };
              })}
              table={
                <Table>
                  <thead className="thead">
                    <tr>
                      <th className="th">Vendor</th>
                      <th className="th">Invoice</th>
                      <th className="th">Due</th>
                      <th className="th text-right">Amount</th>
                      <th className="th">Status</th>
                    </tr>
                  </thead>
                  <tbody className="tbody">
                    {data.items.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-slate-50">
                        <td className="td font-medium">{invoice.vendorName ?? '—'}</td>
                        <td className="td">
                          <Link className="text-brand-700" to={hrefOf(invoice)}>
                            {invoice.invoiceNumber ?? '—'}
                          </Link>
                        </td>
                        <td
                          className={`td ${isOverdue(invoice.dueDate) && invoice.status !== 'PAID' ? 'text-red-600' : ''}`}
                        >
                          {formatDate(invoice.dueDate)}
                        </td>
                        <td className="td text-right">
                          <Money minor={invoice.totalAmount} />
                        </td>
                        <td className="td">
                          <PayableStatus row={invoice} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              }
            />
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onChange={setPage}
            />
          </>
        )}
      </div>
    </>
  );
}

/** An invoice's status; a loan EMI also says it is one, and when it waits on the bank's debit. */
function PayableStatus({
  row,
}: {
  row: { status: string; payableOf?: 'LOAN'; autoDebit?: boolean };
}) {
  if (row.payableOf !== 'LOAN') return <StatusBadge status={row.status} />;
  return (
    <span className="inline-flex flex-wrap gap-1">
      <ToneBadge tone="neutral">Loan EMI</ToneBadge>
      {row.autoDebit ? (
        <ToneBadge tone="attention">Awaiting debit</ToneBadge>
      ) : (
        <StatusBadge status={row.status} />
      )}
    </span>
  );
}
